import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  Calendar, 
  Clock, 
  Wallet, 
  TrendingUp, 
  UserMinus, 
  PieChart as PieIcon,
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronUp,
  ChevronDown,
  BarChart3,
  ExternalLink
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import '../GestionRH.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PIE_COLORS = ['#0F2343', '#3182CE', '#63B3ED', '#A0AEC0', '#2D3748', '#38A169', '#E53E3E'];

export default function DashboardRH({ onNavigate }) {
  const { hasPermission } = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    fetchDashboard();
  }, [selectedYear, selectedMonth]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/rh/dashboard?annee=${selectedYear}&mois=${selectedMonth}`, { headers: headers() });
      setData(res.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) return <div className="rh-dashboard-content"><LoadingSpinner /></div>;

  const stats = [
    { label: 'Effectif Total', value: data.effectifs.total, icon: <Users />, color: 'blue', trend: '+2%' },
    { label: 'Demandes (Mois)', value: data.demandes.totalMois, icon: <Calendar />, color: 'green', trend: '+12%' },
    { label: 'Taux d\'absence', value: `${((data.demandes.totalMois / (data.effectifs.total || 1)) * 5).toFixed(1)}%`, icon: <Activity />, color: 'orange', trend: '-1%' },
    { label: 'Masse Salariale', value: `${(data.salaires.totalNet / 1000).toFixed(1)}k`, icon: <Wallet />, color: 'purple', trend: '+4%' },
  ];

  return (
    <div className="animate-fade-in">
      {/* Stats Grid */}
      <div className="rh-stats-grid">
        {stats.map((stat, i) => (
          <div key={i} className="rh-stat-card">
            <div className={`rh-stat-icon-wrapper ${stat.color}`}>
              {stat.icon}
            </div>
            <div className="rh-stat-info">
              <span className="rh-stat-value">{stat.value}</span>
              <span className="rh-stat-label">{stat.label}</span>
            </div>
            <div className={`rh-stat-trend ${stat.trend.startsWith('+') ? 'up' : 'down'}`} style={{ marginLeft: 'auto', alignSelf: 'flex-start', fontSize: '12px', fontWeight: '700', padding: '4px 8px', borderRadius: '20px', background: stat.trend.startsWith('+') ? '#F0FFF4' : '#FFF5F5', color: stat.trend.startsWith('+') ? '#38A169' : '#E53E3E', display: 'flex', alignItems: 'center', gap: '2px' }}>
              {stat.trend.startsWith('+') ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {stat.trend}
            </div>
          </div>
        ))}
      </div>

      <div className="rh-main-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
        {/* Masse Salariale Chart */}
        <div className="rh-chart-card">
          <div className="rh-widget-header" style={{ marginBottom: '20px' }}>
            <h3>Évolution de la Masse Salariale</h3>
            <BarChart3 size={18} color="var(--rh-blue-dark)" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.evolutionMasse}>
              <defs>
                <linearGradient id="colorMasse" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3182CE" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#3182CE" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748B' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748B' }} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
              />
              <Area type="monotone" dataKey="totalNet" stroke="#3182CE" strokeWidth={3} fillOpacity={1} fill="url(#colorMasse)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Demandes par Département */}
        <div className="rh-chart-card">
          <div className="rh-widget-header" style={{ marginBottom: '20px' }}>
            <h3>Demandes par Département</h3>
            <PieIcon size={18} color="var(--rh-blue-dark)" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ResponsiveContainer width="60%" height={300}>
              <PieChart>
                <Pie
                  data={data.demandesParDept || []}
                  innerRadius={70}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="count"
                  nameKey="_id"
                >
                  {(data.demandesParDept || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ width: '40%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {(data.demandesParDept || []).slice(0, 5).map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600' }}>{item._id}</span>
                    <span style={{ fontSize: '11px', color: 'var(--rh-text-muted)' }}>{item.count} demandes</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rh-main-grid" style={{ gridTemplateColumns: '1.5fr 1fr', gap: '25px', marginTop: '25px' }}>
        {/* Top Performeurs */}
        <div className="rh-table-card">
          <div className="rh-table-header">
            <h3>Top Performeurs du Mois</h3>
            <button className="rh-view-all" onClick={() => onNavigate('rh-evaluations')}>Voir tout</button>
          </div>
          <table className="rh-table">
            <thead>
              <tr>
                <th>Employé</th>
                <th>Score</th>
                <th>Tendance</th>
              </tr>
            </thead>
            <tbody>
              {data.topPerformeurs && data.topPerformeurs.length > 0 ? (
                data.topPerformeurs.map((p, i) => (
                  <tr key={i}>
                    <td>
                      <div className="rh-user-cell">
                        <div className="rh-avatar" style={{ background: '#F1F5F9', color: '#475569' }}>
                          {p.prenom?.[0] || ''}{p.nom?.[0] || ''}
                        </div>
                        <span className="rh-user-name">{(p.prenom || '') + ' ' + (p.nom || '')}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="rh-progress-container" style={{ width: '100px' }}>
                          <div className="rh-progress-fill green" style={{ width: `${p.scoreMoyen}%` }} />
                        </div>
                        <strong style={{ minWidth: '45px' }}>{p.scoreMoyen}%</strong>
                      </div>
                    </td>
                    <td>
                      <span className="rh-status-badge approved" style={{ gap: '4px', padding: '4px 10px' }}>
                        <TrendingUp size={12} /> Top
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center', padding: '40px', color: 'var(--rh-text-muted)' }}>
                    Aucune évaluation finalisée pour ce mois.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Notifications / Alertes */}
        <div className="rh-widget-card" style={{ height: 'auto' }}>
          <div className="rh-widget-header">
            <h3>Alertes RH</h3>
            <AlertTriangle size={18} color="var(--rh-yellow)" />
          </div>
          <div className="rh-list" style={{ marginTop: '15px' }}>
            <div className="rh-list-item" style={{ borderLeft: '4px solid var(--rh-yellow)', background: '#FFFAF0', borderRadius: '12px', padding: '15px' }}>
              <div className="rh-item-text">
                <span className="rh-item-name" style={{ fontSize: '14px' }}>{data.demandes.enAttente} demandes en attente</span>
                <span className="rh-item-sub">Veuillez valider les congés en retard.</span>
              </div>
              <ExternalLink size={16} style={{ cursor: 'pointer' }} onClick={() => onNavigate('rh-demandes')} />
            </div>
            <div className="rh-list-item" style={{ borderLeft: '4px solid #38A169', background: '#F0FFF4', borderRadius: '12px', padding: '15px', marginTop: '12px' }}>
              <div className="rh-item-text">
                <span className="rh-item-name" style={{ fontSize: '14px' }}>Masse salariale calculée</span>
                <span className="rh-item-sub">Les fiches de paie sont prêtes pour {new Date().toLocaleDateString('fr-FR', { month: 'long' })}.</span>
              </div>
              <CheckCircle2 size={16} />
            </div>
            <div className="rh-list-item" style={{ borderLeft: '4px solid #3182CE', background: '#EBF8FF', borderRadius: '12px', padding: '15px', marginTop: '12px' }}>
              <div className="rh-item-text">
                <span className="rh-item-name" style={{ fontSize: '14px' }}>Nouveaux employés (3)</span>
                <span className="rh-item-sub">Bienvenue aux nouveaux collaborateurs.</span>
              </div>
              <Users size={16} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
